import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Mail, Phone, Building2, Briefcase, Globe, Calendar, MessagesSquare, Pencil, Power, Trash2, KeyRound, ShieldAlert, LogIn, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDataLabel } from '@/lib/dataI18n';
import { useApp } from '@/context/AppContext';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import EditUserDialog from '@/components/admin/EditUserDialog';
import ConfirmDeleteDialog from '@/components/shared/ConfirmDeleteDialog';

const initials = (name) => (name || '??').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

const Row = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start gap-3 py-2" data-testid={testId}>
    <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
      <p className="text-sm font-medium truncate">{value || '—'}</p>
    </div>
  </div>
);

export default function UserDetailDialog({ open, onOpenChange, user, companies = [], onViewResponses, onChanged, onIssueCredentials }) {
  const { t, i18n } = useTranslation();
  const tData = useDataLabel();
  const { user: me } = useApp();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isAdmin = me?.role === 'hm_admin';
  if (!user) return null;

  // UAT — Portal Access lifecycle (derived, no schema change).
  const accessState = user.access_state
    || (['inactive', 'disabled'].includes(user.status) ? 'disabled'
      : user.must_change_password ? (user.last_login_at ? 'password_change_pending' : 'first_login_pending')
      : user.last_login_at ? 'active' : 'not_activated');
  const ACCESS_TONE = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    first_login_pending: 'bg-amber-50 text-amber-700 border-amber-200',
    password_change_pending: 'bg-amber-50 text-amber-700 border-amber-200',
    not_activated: 'bg-slate-100 text-slate-600 border-slate-200',
    disabled: 'bg-red-50 text-red-700 border-red-200',
  };
  const accessLabel = t(`pages.users.access.${accessState}`, {
    defaultValue: {
      active: 'Active', first_login_pending: 'First login pending',
      password_change_pending: 'Password change pending',
      not_activated: 'Not activated', disabled: 'Disabled',
    }[accessState],
  });
  const lastLogin = user.last_login_at
    ? new Date(user.last_login_at).toLocaleString(i18n.language?.startsWith('es') ? 'es-ES' : 'en-US',
        { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : t('pages.users.access.never', { defaultValue: 'Never' });

  const doForceChange = async () => {
    try {
      await adminApi.forcePasswordChange(user.id);
      toast.success(t('pages.users.actions.forcedChange', { defaultValue: 'Password change will be required at next session' }));
      onChanged?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createdAt = user.created_at
    ? new Date(user.created_at).toLocaleDateString(i18n.language?.startsWith('es') ? 'es-ES' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : null;

  const toggleStatus = async () => {
    const next = (user.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await adminApi.patchUser(user.id, { status: next });
      toast.success(next === 'active'
        ? t('pages.users.actions.activated', { defaultValue: 'User activated' })
        : t('pages.users.actions.deactivated', { defaultValue: 'User deactivated' }));
      onChanged?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    try {
      await adminApi.deleteUser(user.id);
      toast.success(t('pages.users.actions.deleted', { defaultValue: 'User deleted' }));
      onChanged?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl lg:max-w-3xl" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle className="sr-only">{user.name}</DialogTitle>
            <DialogDescription className="sr-only">{user.email}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14">
              <AvatarFallback className="bg-gradient-primary text-white text-lg font-bold">{initials(user.name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-lg font-semibold truncate" data-testid="user-detail-name">{user.name}</p>
              <p className="text-sm text-muted-foreground truncate" data-testid="user-detail-email">{user.email}</p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                <Badge variant="outline" className="text-xs" data-testid="user-detail-role">
                  {t(`pages.users.roles.${user.role}`, { defaultValue: user.role })}
                </Badge>
                <Badge variant="outline" className="text-xs" data-testid="user-detail-status">{t(`common.statusValues.${user.status || 'active'}`, { defaultValue: user.status || 'active' })}</Badge>
                {(user.role === 'evaluated' || user.is_employee) && (
                  <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/30" data-testid="user-detail-collaborator-badge">
                    {t('phase3.person.collaboratorBadge', { defaultValue: 'Organizational collaborator' })}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <Separator className="my-2" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Row icon={Mail} label={t('pages.users.email', { defaultValue: 'Email' })} value={user.email} testId="user-detail-row-email" />
            <Row icon={Phone} label={t('pages.users.phone', { defaultValue: 'Phone' })} value={user.phone} testId="user-detail-row-phone" />
            <Row icon={Building2} label={t('pages.users.company', { defaultValue: 'Company' })} value={user.company_name ? tData(user.company_name) : t('pages.users.internal', { defaultValue: 'Internal CTH user' })} testId="user-detail-row-company" />
            <Row icon={Briefcase} label={t('pages.users.position', { defaultValue: 'Position' })} value={user.position} testId="user-detail-row-position" />
            <Row icon={Globe} label={t('pages.users.locale', { defaultValue: 'Locale' })} value={user.locale ? user.locale.toUpperCase() : null} testId="user-detail-row-locale" />
            <Row icon={Calendar} label={t('pages.users.createdAt', { defaultValue: 'Created' })} value={createdAt} testId="user-detail-row-created" />
          </div>

          <Separator className="my-2" />

          {/* UAT — explicit Portal Access lifecycle section */}
          <div className="rounded-md border p-3 space-y-2" data-testid="user-detail-portal-access">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5">
                <LogIn className="h-3.5 w-3.5" />
                {t('pages.users.access.title', { defaultValue: 'Portal access' })}
              </p>
              <Badge variant="outline" className={`text-xs ${ACCESS_TONE[accessState]}`} data-testid="user-detail-access-state">
                {accessLabel}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-x-4 text-xs text-muted-foreground">
              <p data-testid="user-detail-last-login">
                {t('pages.users.access.lastLogin', { defaultValue: 'Last login' })}: <span className="font-medium text-foreground">{lastLogin}</span>
              </p>
              <p data-testid="user-detail-must-change">
                {t('pages.users.access.mustChange', { defaultValue: 'Must change password' })}: <span className="font-medium text-foreground">{user.must_change_password ? t('common.yes', { defaultValue: 'Yes' }) : t('common.no', { defaultValue: 'No' })}</span>
              </p>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => onIssueCredentials?.(user)} data-testid="user-detail-issue-credentials">
                  <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                  {['not_activated', 'first_login_pending'].includes(accessState)
                    ? t('pages.users.access.activate', { defaultValue: 'Activate portal access' })
                    : t('pages.users.access.resetTemp', { defaultValue: 'Reset temporary password' })}
                </Button>
                {!user.must_change_password && accessState !== 'disabled' && (
                  <Button variant="outline" size="sm" onClick={doForceChange} data-testid="user-detail-force-change">
                    <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />
                    {t('pages.users.access.forceChange', { defaultValue: 'Force password change' })}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={toggleStatus}
                        className={accessState === 'disabled' ? '' : 'text-destructive border-destructive/40 hover:bg-destructive/10'}
                        data-testid="user-detail-toggle-access">
                  <Power className="h-3.5 w-3.5 mr-1.5" />
                  {accessState === 'disabled'
                    ? t('pages.users.access.enable', { defaultValue: 'Enable portal access' })
                    : t('pages.users.access.disable', { defaultValue: 'Disable portal access' })}
                </Button>
              </div>
            )}
          </div>

          <Separator className="my-2" />

          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              {isAdmin && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="user-detail-edit">
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    {t('pages.users.actions.edit', { defaultValue: 'Edit user' })}
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setDeleteOpen(true)}
                    className="text-destructive border-destructive/40 hover:bg-destructive/10"
                    data-testid="user-detail-delete"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    {t('pages.users.actions.delete', { defaultValue: 'Delete permanently' })}
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="user-detail-close">
                {t('common.close', { defaultValue: 'Close' })}
              </Button>
              {/* UAT B — role-aware primary action */}
              {user.role === 'evaluated' && (
                <Button onClick={() => { onOpenChange(false); onViewResponses?.(user); }} className="gap-1.5" data-testid="user-detail-view-responses">
                  <MessagesSquare className="h-4 w-4" />
                  {t('pages.users.viewResponses', { defaultValue: 'View responses' })}
                </Button>
              )}
              {user.role === 'hm_advisor' && (
                <Button onClick={() => { onOpenChange(false); navigate('/admin/advisors'); }} className="gap-1.5" data-testid="user-detail-view-advisor">
                  <ExternalLink className="h-4 w-4" />
                  {t('pages.users.viewAdvisorBook', { defaultValue: 'View advisor workspace' })}
                </Button>
              )}
              {user.role === 'client_admin' && user.company_id && (
                <Button onClick={() => { onOpenChange(false); navigate(`/admin/clients/${user.company_id}`); }} className="gap-1.5" data-testid="user-detail-view-company">
                  <Building2 className="h-4 w-4" />
                  {t('pages.users.viewCompany', { defaultValue: 'View company' })}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EditUserDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        user={user}
        companies={companies}
        onUpdated={() => { onChanged?.(); onOpenChange(false); }}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('pages.users.actions.deleteConfirmTitle', { defaultValue: 'Delete user permanently?' })}
        description={t('pages.users.actions.deleteConfirmDesc', { defaultValue: 'This will remove the user and cascade-clean their notifications, consent, personal profile, certificates, and TalentFix case participation. Type the email to confirm.' })}
        expectedInput={user.email || ''}
        inputPlaceholder={t('pages.users.actions.deleteConfirmPh', { defaultValue: 'Type the email to confirm' })}
        confirmLabel={t('pages.users.actions.delete', { defaultValue: 'Delete permanently' })}
        testIdPrefix="user-delete"
        onConfirm={doDelete}
      />
    </>
  );
}
